import { useEffect, useMemo, useState } from "react";
import { ArrowUpDown, Search } from "lucide-react";
import { api, formatPHPhone } from "../../lib/api";
import {
  BrutalCard,
  BrutalInput,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

const STATUS_COLORS = {
  registered: "yellow",
  "checked-in": "green",
  "checked-out": "teal",
  overdue: "red",
};

export default function AdminRegistry() {
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/registry").then(({ data }) => setRows(data));
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (filter) {
      const q = filter.toLowerCase();
      r = r.filter(
        (x) =>
          x.name.toLowerCase().includes(q) ||
          x.contact.toLowerCase().includes(q) ||
          x.zone_name.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") r = r.filter((x) => x.status === statusFilter);
    return [...r].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, filter, statusFilter]);

  const Header = ({ k, children }) => (
    <th
      onClick={() => setSort({ key: k, dir: sort.key === k && sort.dir === "asc" ? "desc" : "asc" })}
      className="cursor-pointer text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D] select-none"
      data-testid={`registry-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {children} <ArrowUpDown className="h-3 w-3 opacity-60" />
      </span>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="admin-registry">
      <SectionTitle kicker="Camper Registry" title="All campers, all statuses" />

      <BrutalCard className="p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B4E9E]" />
          <BrutalInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search name, contact, zone..."
            className="pl-9"
            data-testid="registry-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="registry-status-filter">
          {["all", "registered", "checked-in", "checked-out", "overdue"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 border-2 border-[#3D1E6D] text-[10px] font-bold uppercase tracking-widest ${
                statusFilter === s
                  ? "bg-[#E5247C] text-white shadow-[3px_3px_0_#3D1E6D]"
                  : "bg-white"
              }`}
              data-testid={`registry-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </BrutalCard>

      <BrutalCard className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="registry-table">
          <thead className="bg-[#FFB400]">
            <tr>
              <Header k="name">Name</Header>
              <Header k="zone_name">Zone</Header>
              <Header k="camping_type">Type</Header>
              <Header k="contact">Contact</Header>
              <Header k="age">Age</Header>
              <Header k="status">Status</Header>
              <Header k="check_in">Check-in</Header>
              <Header k="check_out">Check-out</Header>
              <Header k="payment_status">Payment</Header>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr
                key={r.camper_id}
                className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"}
                data-testid={`registry-row-${r.camper_id}`}
              >
                <td className="px-3 py-2 font-bold">
                  {r.name}
                  {r.is_primary && (
                    <Badge color="orange" className="ml-2">Primary</Badge>
                  )}
                </td>
                <td className="px-3 py-2">{r.zone_name}</td>
                <td className="px-3 py-2 uppercase">{r.camping_type}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">
                  {r.contact.includes("@") ? r.contact : formatPHPhone(r.contact)}
                </td>
                <td className="px-3 py-2">{r.age}</td>
                <td className="px-3 py-2">
                  <Badge color={STATUS_COLORS[r.status] || "yellow"}>{r.status}</Badge>
                </td>
                <td className="px-3 py-2">{r.check_in}</td>
                <td className="px-3 py-2">{r.check_out}</td>
                <td className="px-3 py-2">
                  <Badge color={r.payment_status === "paid" ? "green" : "amber"}>
                    {r.payment_status}
                  </Badge>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-[#6B4E9E]">
                  No campers match your filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </BrutalCard>
    </div>
  );
}
